/**
 * Notion-style URL helpers (Phase 5 Wave B).
 *
 * Doc URLs follow the shape `/wiki/{slug}-{8charTail}` where:
 *
 *   - `slug` is the kebab-cased + ASCII-folded + lowercased
 *     `wiki_nodes.name` (purely decorative; cosmetic only).
 *   - `tail` is the first 8 hex chars of the node UUID with dashes
 *     stripped, lowercased (load-bearing — the BE resolver matches on
 *     this).
 *
 * The URL stays stable across rename + move: only the tail is the
 * resolver, and the tail is derived from `id` (immutable). A rename
 * changes the slug; the FE's legacy `/wiki/d/:uuid` redirect path
 * normalizes any pre-rename link to the current canonical URL.
 *
 * Slug invariants:
 *
 *   - lowercase ASCII alphanumerics + hyphens only
 *   - diacritics stripped via NFKD decomposition (é → e)
 *   - emoji / non-printable / non-ASCII chars dropped
 *   - leading + trailing + runs of hyphens collapsed
 *   - capped at 80 chars to avoid pathological URLs
 *   - falls back to `'untitled'` when the result is empty
 *
 * Collision math: ~1.2% probability per 100k docs/tenant on the
 * 8-char tail. The BE returns `409 wiki_tail_collision` when it hits
 * one; the FE falls back to the canonical `/wiki/d/:uuid` URL.
 */

/** Cap the kebab-cased slug at this many characters to keep URLs sane. */
const MAX_SLUG_LENGTH = 80

/** Fallback slug for names that strip down to nothing. */
const FALLBACK_SLUG = 'untitled'

/**
 * Tail length — first N hex chars of the UUID with dashes stripped.
 * Mirrored in the BE partial expression index (migration `0048`) and the
 * BE resolver's regex (`^[0-9a-f]{8}$`). Changing this value here means
 * the index, the regex, and `parseDocPathTail` ALL need to move
 * together.
 */
const TAIL_LENGTH = 8

/**
 * Slugify a wiki node name to the decorative URL fragment. The result
 * is purely cosmetic — the BE resolves docs from the tail, not the
 * slug, so any subset of the rules can change without breaking
 * existing URLs.
 */
export function slugifyWikiName(name: string): string {
  if (typeof name !== 'string') return FALLBACK_SLUG

  const slug = name
    .normalize('NFKD')
    // Strip combining diacritical marks (U+0300–U+036F) left over after
    // NFKD decomposition. Without this, "café" → "cafe" stays as "café"
    // because the combining acute survives the lowercase pass.
    .replace(/\p{Mn}/gu, '')
    .toLowerCase()
    // Drop everything outside `[a-z0-9 -]`. Emoji, CJK, punctuation —
    // all go. Spaces survive so the next replace can collapse them.
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    // Spaces → hyphens.
    .replace(/\s+/g, '-')
    // Collapse runs.
    .replace(/-+/g, '-')
    // Trim leading / trailing hyphens (e.g., a name starting with a
    // dropped emoji would land with a leading hyphen).
    .replace(/^-+|-+$/g, '')
    .slice(0, MAX_SLUG_LENGTH)

  return slug.length > 0 ? slug : FALLBACK_SLUG
}

/**
 * Extract the first 8 hex chars from a UUID (with dashes stripped),
 * lowercased. This is what the BE resolver matches against; treat it
 * as opaque on the FE side beyond the regex shape.
 */
export function shortIdTail(nodeId: string): string {
  if (typeof nodeId !== 'string') return ''
  return nodeId.replace(/-/g, '').slice(0, TAIL_LENGTH).toLowerCase()
}

/**
 * Build the canonical Notion-style doc path for a node. Callers that
 * already have the node row (with `name`) prefer this over the legacy
 * `/wiki/d/{id}` shape; the redirect route handles legacy callers that
 * only have the id.
 */
export function buildDocPath(node: { id: string; name: string }): string {
  return `/wiki/${slugifyWikiName(node.name)}-${shortIdTail(node.id)}`
}

/**
 * Parse the tail from a URL fragment of the shape `<slug>-<tail>`. The
 * tail is the last `-`-separated segment, and only resolves when it
 * matches the 8-char lowercase hex regex; everything else returns
 * null (the route file falls through to a 404 surface).
 *
 * The slug itself can contain hyphens, so a naive `split('-').pop()`
 * works — we just need the trailing 8-hex-char segment to match.
 */
export function parseDocPathTail(slugWithTail: string): string | null {
  if (typeof slugWithTail !== 'string' || slugWithTail.length === 0) return null
  const idx = slugWithTail.lastIndexOf('-')
  if (idx < 0) return null
  const candidate = slugWithTail.slice(idx + 1)
  if (!/^[0-9a-f]{8}$/.test(candidate)) return null
  return candidate
}

/**
 * Pick the node a colliding pretty URL means. Tails collide (two nodes
 * share their first 8 hex chars), but the slug in front of the tail
 * usually still tells them apart — so among the loaded nodes, the answer
 * is the ONE non-folder whose canonical path is exactly this one. Returns
 * null when nothing, or more than one thing, matches; the caller then
 * shows the collision message rather than guessing.
 */
export function resolveTailCollision<T extends { id: string; name: string; kind: string }>(
  slugWithTail: string,
  nodes: Iterable<T>,
): T | null {
  const tail = parseDocPathTail(slugWithTail)
  if (tail === null) return null
  const path = `/wiki/${slugWithTail}`
  let match: T | null = null
  for (const node of nodes) {
    if (node.kind === 'folder' || shortIdTail(node.id) !== tail) continue
    if (buildDocPath(node) !== path) continue
    if (match) return null
    match = node
  }
  return match
}
